// src/pages/VerifyCodeScreen.jsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";

import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Clock3,
  KeyRound,
  LoaderCircle,
  Mail,
  RefreshCw,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

import "../sass/pages/verifyCode.scss";

import Logo from "../assets/logo.webp";
import { API_BASE_URL } from "../config/api";
import { useToast } from "../context/ToastContext";

const API_BASE = String(
  API_BASE_URL ||
    process.env.REACT_APP_API_URL ||
    "https://api.psyassist.com.br"
).replace(/\/+$/, "");

const ENDPOINT_VERIFY_CODE =
  `${API_BASE}/psy/user/verify-reset-code`;

const ENDPOINT_SEND_CODE =
  `${API_BASE}/psy/user/send-reset-code`;

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 60;

const RESET_STORAGE_KEYS = {
  EMAIL: "psy_password_reset_email",
  REQUESTED_AT: "psy_password_reset_requested_at",
  VERIFIED_AT: "psy_password_reset_verified_at",
  RESET_TOKEN: "psy_password_reset_token",
};

const createEmptyDigits = () =>
  Array.from(
    { length: CODE_LENGTH },
    () => ""
  );

const onlyDigits = (value = "") =>
  String(value || "").replace(/\D/g, "");

const readStorage = (storage, key) => {
  try {
    return storage.getItem(key) || "";
  } catch {
    return "";
  }
};

const writeStorage = (storage, key, value) => {
  try {
    storage.setItem(
      key,
      String(value ?? "")
    );

    return true;
  } catch {
    return false;
  }
};

const removeStorage = (storage, key) => {
  try {
    storage.removeItem(key);
  } catch {
    // O fluxo pode continuar mesmo sem acesso ao storage.
  }
};

const maskEmail = (email = "") => {
  const normalized =
    String(email || "").trim();

  const [localPart, domain] =
    normalized.split("@");

  if (!localPart || !domain) {
    return normalized ||
      "E-mail não identificado";
  }

  const visibleStart =
    localPart.slice(
      0,
      Math.min(2, localPart.length)
    );

  const hiddenLength =
    Math.max(
      localPart.length -
        visibleStart.length,
      3
    );

  return `${visibleStart}${"*".repeat(
    hiddenLength
  )}@${domain}`;
};

const readApiPayload =
  async (response) => {
    const contentType =
      response.headers.get(
        "content-type"
      ) || "";

    try {
      if (
        contentType.includes(
          "application/json"
        )
      ) {
        const payload =
          await response.json();

        return payload &&
          typeof payload === "object"
          ? payload
          : {};
      }

      const text =
        await response.text();

      return text
        ? {
            message: text,
          }
        : {};
    } catch {
      return {};
    }
  };

const getRemainingCooldown = (
  requestedAt
) => {
  if (!requestedAt) return 0;

  const requestedTime =
    new Date(requestedAt).getTime();

  if (
    Number.isNaN(requestedTime)
  ) {
    return 0;
  }

  const elapsedSeconds =
    Math.floor(
      (Date.now() -
        requestedTime) /
        1000
    );

  return Math.max(
    RESEND_COOLDOWN_SECONDS -
      elapsedSeconds,
    0
  );
};

const getVerifyErrorMessage = (
  payload,
  status
) => {
  if (status === 400) {
    return (
      payload?.message ||
      "O código informado não é válido."
    );
  }

  if (
    status === 401 ||
    status === 403
  ) {
    return (
      payload?.message ||
      "O código é inválido ou expirou."
    );
  }

  if (status === 410) {
    return (
      payload?.message ||
      "Este código expirou. Solicite um novo código."
    );
  }

  if (status === 429) {
    return (
      payload?.message ||
      "Muitas tentativas foram realizadas. Aguarde antes de tentar novamente."
    );
  }

  if (status >= 500) {
    return "O serviço de verificação está temporariamente indisponível.";
  }

  return (
    payload?.message ||
    "Não foi possível validar o código."
  );
};

const VerifyCodeScreen = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();

  const inputsRef = useRef([]);

  const email = useMemo(() => {
    return String(
      location.state?.email ||
        readStorage(
          sessionStorage,
          RESET_STORAGE_KEYS.EMAIL
        ) ||
        ""
    )
      .trim()
      .toLowerCase();
  }, [location.state?.email]);

  const initialRequestedAt =
    useMemo(() => {
      return (
        location.state
          ?.requestedAt ||
        readStorage(
          sessionStorage,
          RESET_STORAGE_KEYS.REQUESTED_AT
        ) ||
        ""
      );
    }, [
      location.state?.requestedAt,
    ]);

  const [
    digits,
    setDigits,
  ] = useState(
    createEmptyDigits
  );

  const [
    codeError,
    setCodeError,
  ] = useState("");

  const [
    verifying,
    setVerifying,
  ] = useState(false);

  const [
    resending,
    setResending,
  ] = useState(false);

  const [
    resendRemaining,
    setResendRemaining,
  ] = useState(() =>
    getRemainingCooldown(
      initialRequestedAt
    )
  );

  const code = useMemo(
    () => digits.join(""),
    [digits]
  );

  const isBusy =
    verifying || resending;

  useEffect(() => {
    if (!email) return;

    writeStorage(
      sessionStorage,
      RESET_STORAGE_KEYS.EMAIL,
      email
    );

    window.setTimeout(() => {
      inputsRef.current[0]?.focus();
    }, 100);
  }, [email]);

  useEffect(() => {
    setResendRemaining(
      getRemainingCooldown(
        initialRequestedAt
      )
    );
  }, [initialRequestedAt]);

  useEffect(() => {
    if (
      resendRemaining <= 0
    ) {
      return undefined;
    }

    const timer =
      window.setInterval(() => {
        setResendRemaining(
          (current) =>
            Math.max(
              current - 1,
              0
            )
        );
      }, 1000);

    return () => {
      window.clearInterval(
        timer
      );
    };
  }, [resendRemaining]);

  const focusInput =
    useCallback((index) => {
      const safeIndex =
        Math.max(
          0,
          Math.min(
            index,
            CODE_LENGTH - 1
          )
        );

      inputsRef.current[
        safeIndex
      ]?.focus();

      inputsRef.current[
        safeIndex
      ]?.select?.();
    }, []);
    const clearCodeError = useCallback(() => {
      setCodeError("");
    }, []);

  const applyCodeSequence =
    useCallback(
      (
        sequence,
        startIndex = 0
      ) => {
        const numbers =
          onlyDigits(sequence)
            .slice(
              0,
              CODE_LENGTH -
                startIndex
            )
            .split("");

        if (!numbers.length) {
          return;
        }

        setDigits((current) => {
          const next = [
            ...current,
          ];

          numbers.forEach(
            (number, offset) => {
              const target =
                startIndex +
                offset;

              if (
                target <
                CODE_LENGTH
              ) {
                next[target] =
                  number;
              }
            }
          );

          return next;
        });

        clearCodeError();

        const lastFilledIndex =
          Math.min(
            startIndex +
              numbers.length,
            CODE_LENGTH - 1
          );

        window.setTimeout(() => {
          focusInput(
            lastFilledIndex
          );
        }, 0);
      },
      [focusInput, clearCodeError]
    );

  const handleChangeDigit = (
    index,
    value
  ) => {
    const numbers =
      onlyDigits(value);

    if (numbers.length > 1) {
      applyCodeSequence(
        numbers,
        index
      );

      return;
    }

    const digit =
      numbers.slice(-1);

    setDigits((current) => {
      const next = [
        ...current,
      ];

      next[index] = digit;

      return next;
    });

    clearCodeError();

    if (
      digit &&
      index <
        CODE_LENGTH - 1
    ) {
      focusInput(index + 1);
    }
  };

  const handleKeyDown = (
    index,
    event
  ) => {
    if (
      event.key ===
        "Backspace" &&
      !digits[index] &&
      index > 0
    ) {
      event.preventDefault();
      focusInput(index - 1);
      return;
    }

    if (
      event.key ===
      "ArrowLeft"
    ) {
      event.preventDefault();
      focusInput(index - 1);
      return;
    }

    if (
      event.key ===
      "ArrowRight"
    ) {
      event.preventDefault();
      focusInput(index + 1);
    }
  };

  const handlePaste = (
    event
  ) => {
    const pasted =
      onlyDigits(
        event.clipboardData.getData(
          "text"
        )
      );

    if (!pasted) return;

    event.preventDefault();

    setDigits(
      createEmptyDigits()
    );

    applyCodeSequence(
      pasted,
      0
    );
  };

  const validateCode = () => {
    if (!email) {
      toast.warning({
        title:
          "E-mail não identificado",
        message:
          "Retorne à recuperação de senha e informe seu e-mail novamente.",
        duration: 0,
      });

      return false;
    }

    if (
      code.length !==
      CODE_LENGTH
    ) {
      setCodeError(
        "Preencha os seis dígitos do código."
      );

      const firstEmptyIndex =
        digits.findIndex(
          (digit) => !digit
        );

      focusInput(
        firstEmptyIndex >= 0
          ? firstEmptyIndex
          : 0
      );

      return false;
    }

    setCodeError("");
    return true;
  };

  const handleValidate =
    async (event) => {
      event.preventDefault();

      if (isBusy) return;
      if (!validateCode()) {
        return;
      }

      setVerifying(true);

      try {
        const response =
          await fetch(
            ENDPOINT_VERIFY_CODE,
            {
              method: "POST",
              headers: {
                "Content-Type":
                  "application/json",
                Accept:
                  "application/json",
              },
              body:
                JSON.stringify({
                  email,
                  code,
                }),
            }
          );

        const payload =
          await readApiPayload(
            response
          );

        const isValid =
          response.ok &&
          (
            payload?.valid ===
              true ||
            payload?.success ===
              true
          );

        if (!isValid) {
          const verifyError =
            new Error(
              getVerifyErrorMessage(
                payload,
                response.status
              )
            );

          verifyError.status =
            response.status;

          throw verifyError;
        }

        const resetToken =
          String(
            payload?.resetToken ||
              payload
                ?.verificationToken ||
              payload?.token ||
              ""
          ).trim();

        writeStorage(
          sessionStorage,
          RESET_STORAGE_KEYS.EMAIL,
          email
        );

        writeStorage(
          sessionStorage,
          RESET_STORAGE_KEYS.VERIFIED_AT,
          new Date().toISOString()
        );

        if (resetToken) {
          writeStorage(
            sessionStorage,
            RESET_STORAGE_KEYS.RESET_TOKEN,
            resetToken
          );
        } else {
          removeStorage(
            sessionStorage,
            RESET_STORAGE_KEYS.RESET_TOKEN
          );
        }

        setDigits(
          createEmptyDigits()
        );

        toast.success({
          title:
            "Código confirmado",
          message:
            "Agora você pode criar uma nova senha para sua conta.",
        });

        navigate(
          "/reset-password",
          {
            replace: true,
            state: {
              email,
              resetToken,
              verifiedAt:
                new Date().toISOString(),
            },
          }
        );
      } catch (error) {
        console.error(
          "Erro ao validar o código:",
          error
        );

        const invalidCode =
          [
            400,
            401,
            403,
            410,
          ].includes(
            error?.status
          );

        if (invalidCode) {
          setCodeError(
            error?.message ||
              "Código inválido ou expirado."
          );

          setDigits(
            createEmptyDigits()
          );

          window.setTimeout(() => {
            focusInput(0);
          }, 80);

          return;
        }

        toast.error({
          title:
            "Não foi possível validar",
          message:
            error?.message ||
            "Verifique sua conexão e tente novamente.",
          duration:
            error?.status ===
            429
              ? 0
              : 6000,
        });
      } finally {
        setVerifying(false);
      }
    };

  const handleResend =
    async () => {
      if (
        isBusy ||
        resendRemaining > 0
      ) {
        return;
      }

      if (!email) {
        navigate(
          "/forgot-password"
        );

        return;
      }

      setResending(true);

      try {
        const response =
          await fetch(
            ENDPOINT_SEND_CODE,
            {
              method: "POST",
              headers: {
                "Content-Type":
                  "application/json",
                Accept:
                  "application/json",
              },
              body:
                JSON.stringify({
                  email,
                }),
            }
          );

        const payload =
          await readApiPayload(
            response
          );

        if (!response.ok) {
          const resendError =
            new Error(
              payload?.message ||
                (
                  response.status ===
                  429
                    ? "Muitas solicitações foram realizadas. Aguarde antes de reenviar."
                    : "Não foi possível reenviar o código."
                )
            );

          resendError.status =
            response.status;

          throw resendError;
        }

        const requestedAt =
          new Date().toISOString();

        writeStorage(
          sessionStorage,
          RESET_STORAGE_KEYS.REQUESTED_AT,
          requestedAt
        );

        setResendRemaining(
          RESEND_COOLDOWN_SECONDS
        );

        setDigits(
          createEmptyDigits()
        );

        setCodeError("");

        window.setTimeout(() => {
          focusInput(0);
        }, 80);

        toast.success({
          title:
            "Novo código solicitado",
          message:
            payload?.message ||
            "Caso exista uma conta para este e-mail, um novo código será enviado.",
        });
      } catch (error) {
        console.error(
          "Erro ao reenviar código:",
          error
        );

        toast.error({
          title:
            "Não foi possível reenviar",
          message:
            error?.message ||
            "Tente novamente em alguns instantes.",
          duration:
            error?.status ===
            429
              ? 0
              : 6000,
        });
      } finally {
        setResending(false);
      }
    };

  const handleChangeEmail = () => {
    if (isBusy) return;

    navigate(
      "/forgot-password",
      {
        replace: true,
      }
    );
  };

  const maskedEmail =
    maskEmail(email);

  return (
    <main className="psy-verify-page">
      <div
        className="psy-verify-background"
        aria-hidden="true"
      >
        <span className="psy-verify-background__orb psy-verify-background__orb--one" />
        <span className="psy-verify-background__orb psy-verify-background__orb--two" />
        <span className="psy-verify-background__grid" />
      </div>

      <div className="psy-verify-layout">
        <aside className="psy-verify-aside">
          <Link
            to="/"
            className="psy-verify-brand"
            aria-label="Voltar para a página inicial"
          >
            <img
              src={Logo}
              alt="PsyAssist Pro"
            />

            <div>
              <strong>
                PsyAssist Pro
              </strong>

              <span>
                Verificação protegida de acesso
              </span>
            </div>
          </Link>

          <div className="psy-verify-aside__content">
            <span className="psy-verify-kicker">
              <Sparkles size={15} />
              Etapa de segurança
            </span>

            <h1>
              Confirme que o acesso pertence a
              você.
            </h1>

            <p>
              Utilize o código temporário
              enviado ao seu e-mail. Ele será
              validado antes da criação da nova
              senha.
            </p>

            <div className="psy-verify-assurances">
              <div className="psy-verify-assurance">
                <span>
                  <Clock3 size={19} />
                </span>

                <div>
                  <strong>
                    Validade limitada
                  </strong>

                  <small>
                    Códigos antigos devem ser
                    invalidados quando um novo
                    envio é solicitado.
                  </small>
                </div>
              </div>

              <div className="psy-verify-assurance">
                <span>
                  <ShieldCheck size={19} />
                </span>

                <div>
                  <strong>
                    Uso único
                  </strong>

                  <small>
                    Depois da confirmação, o
                    código não deve poder ser
                    reutilizado.
                  </small>
                </div>
              </div>
            </div>
          </div>

          <div className="psy-verify-security-card">
            <div>
              <KeyRound size={19} />
            </div>

            <p>
              <strong>
                Mantenha o código em sigilo
              </strong>

              O suporte do PsyAssist Pro nunca
              solicitará os seis dígitos por
              telefone, mensagem ou chat.
            </p>
          </div>
        </aside>

        <section className="psy-verify-panel">
          <div className="psy-verify-panel__top">
            <span>
              Código enviado para
            </span>

            <strong>
              {maskedEmail}
            </strong>
          </div>

          <div className="psy-verify-content">
            <header className="psy-verify-header">
              <div
                className="psy-verify-header__icon"
                aria-hidden="true"
              >
                <KeyRound size={22} />
              </div>

              <div>
                <span>
                  Confirmação do código
                </span>

                <h2>
                  Verifique seu e-mail
                </h2>

                <p>
                  Digite os seis números
                  recebidos. Você também pode
                  colar o código completo no
                  primeiro campo.
                </p>
              </div>
            </header>

            {!email ? (
              <section
                className="psy-verify-missing"
                role="alert"
              >
                <div>
                  <Mail size={20} />
                </div>

                <div>
                  <strong>
                    E-mail não identificado
                  </strong>

                  <p>
                    O fluxo de recuperação
                    expirou ou foi aberto sem
                    os dados necessários.
                  </p>
                </div>

                <button
                  type="button"
                  onClick={
                    handleChangeEmail
                  }
                >
                  Informar e-mail
                </button>
              </section>
            ) : (
              <form
                className="psy-verify-form"
                onSubmit={
                  handleValidate
                }
                noValidate
                aria-busy={isBusy}
              >
                <div className="psy-verify-code-section">
                  <div className="psy-verify-code-section__header">
                    <label id="psy-verify-code-label">
                      Código de verificação
                    </label>

                    <span>
                      6 dígitos
                    </span>
                  </div>

                  <div
                    className={`psy-verify-code-grid ${
                      codeError
                        ? "psy-verify-code-grid--error"
                        : ""
                    }`}
                    onPaste={
                      handlePaste
                    }
                    role="group"
                    aria-labelledby="psy-verify-code-label"
                    aria-describedby={
                      codeError
                        ? "psy-verify-code-error"
                        : "psy-verify-code-help"
                    }
                  >
                    {digits.map(
                      (
                        value,
                        index
                      ) => (
                        <input
                          key={index}
                          ref={(element) => {
                            inputsRef.current[
                              index
                            ] =
                              element;
                          }}
                          id={`psy-verify-digit-${index + 1}`}
                          type="text"
                          inputMode="numeric"
                          autoComplete={
                            index === 0
                              ? "one-time-code"
                              : "off"
                          }
                          pattern="[0-9]*"
                          maxLength={1}
                          value={value}
                          onChange={(
                            event
                          ) =>
                            handleChangeDigit(
                              index,
                              event
                                .target
                                .value
                            )
                          }
                          onKeyDown={(
                            event
                          ) =>
                            handleKeyDown(
                              index,
                              event
                            )
                          }
                          disabled={
                            isBusy
                          }
                          aria-label={`Dígito ${index + 1} do código`}
                          aria-invalid={Boolean(
                            codeError
                          )}
                        />
                      )
                    )}
                  </div>

                  {codeError ? (
                    <p
                      id="psy-verify-code-error"
                      className="psy-verify-code-error"
                      role="alert"
                    >
                      {codeError}
                    </p>
                  ) : (
                    <p
                      id="psy-verify-code-help"
                      className="psy-verify-code-help"
                    >
                      O código pode levar alguns
                      instantes para chegar.
                    </p>
                  )}
                </div>

                <div className="psy-verify-resend">
                  <div>
                    <RefreshCw size={17} />

                    <p>
                      <strong>
                        Não recebeu?
                      </strong>

                      Verifique spam e promoções
                      antes de solicitar um
                      novo envio.
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={
                      handleResend
                    }
                    disabled={
                      isBusy ||
                      resendRemaining >
                        0
                    }
                  >
                    {resending
                      ? "Reenviando..."
                      : resendRemaining >
                          0
                        ? `Reenviar em ${resendRemaining}s`
                        : "Reenviar código"}
                  </button>
                </div>

                <button
                  type="submit"
                  className="psy-verify-primary"
                  disabled={
                    isBusy
                  }
                >
                  {verifying ? (
                    <>
                      <LoaderCircle
                        size={18}
                        className="psy-verify-spinner"
                      />

                      Validando código...
                    </>
                  ) : (
                    <>
                      Confirmar e continuar
                      <ArrowRight size={18} />
                    </>
                  )}
                </button>

                <button
                  type="button"
                  className="psy-verify-secondary"
                  onClick={
                    handleChangeEmail
                  }
                  disabled={
                    isBusy
                  }
                >
                  <ArrowLeft size={18} />
                  Corrigir e-mail
                </button>
              </form>
            )}

            <section className="psy-verify-next-step">
              <CheckCircle2 size={18} />

              <p>
                <strong>
                  Próxima etapa
                </strong>

                Depois da confirmação, você
                criará uma nova senha. O
                código informado não será
                reutilizado.
              </p>
            </section>
          </div>

          <footer className="psy-verify-footer">
            <ShieldCheck size={14} />

            <p>
              A redefinição deve ser autorizada
              por um token temporário gerado
              pelo servidor após esta etapa.
            </p>
          </footer>
        </section>
      </div>
    </main>
  );
};

export default VerifyCodeScreen;
